import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Compass,
  Download,
  Filter,
  Globe2,
  Info,
  Languages,
  Layers,
  Settings2,
  Sparkles,
  TrendingUp,
  X,
} from 'lucide-react';
import { keywordDifficultyTier, type Intent, type Keyword, type KeywordRun } from '@seo/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/spinner';
import { DetailError } from '@/components/detail-page';
import { EmptyState } from '@/components/empty-state';
import { StatusPill } from '@/components/status-pill';
import { ClusterBubbles } from '@/components/cluster-bubbles';
import { useToast, toastError } from '@/components/ui/toast';
import { useProject } from '@/features/projects/use-project';
import {
  useKeywordRun,
  useKeywordRunClusters,
  useKeywordRunKeywords,
  type KeywordFilters,
} from '@/features/keywords/use-keyword-run';
import { TrackKeywordButton } from '@/features/ranks/components/track-keyword-button';
import {
  TrackKeywordsBulkDialog,
  type BulkCandidate,
} from '@/features/ranks/components/track-keywords-bulk-dialog';
import { BriefCreateButton } from '@/features/briefs/components/brief-create-button';
import { CompetitorAnalysisCreateButton } from '@/features/competitor-analyses/components/competitor-analysis-create-button';
import { PageHeader } from '@/components/page-header';
import { GlassSurface } from '@/components/glow-card';
import { KpiTile, type KpiAccent } from '@/components/kpi-tile';
import { DataTable, type DataTableColumn } from '@/components/data-table';
import { fadeUp, stagger } from '@/components/motion';
import { useAuthStore } from '@/lib/auth';
import { downloadAuthed } from '@/lib/download';
import { cn } from '@/lib/cn';

type SortKey = 'term' | 'volume' | 'difficulty' | 'opportunity' | 'your_position';
type SortDir = 'asc' | 'desc';

const RANK_CHECK_SKIP_CODES = new Set(['RANK_CHECK_SKIPPED', 'DFS_NOT_CONFIGURED']);

const INTENT_VARIANT: Record<Intent, 'info' | 'warning' | 'success' | 'secondary'> = {
  informational: 'info',
  navigational: 'secondary',
  commercial: 'warning',
  transactional: 'success',
};

/**
 * Colour tone for keyword difficulty (0-100).
 * Cutoffs match the Ahrefs / Moz / SEMrush convention exported from
 * `@seo/types.keywordDifficultyTier()`:
 *   Easy 0-10 → green
 *   Medium 11-30 → emerald
 *   Hard 31-70 → amber
 *   Super Hard 71-100 → rose
 */
const DIFFICULTY_TONE = (n: number | null): string => {
  if (n === null) return 'text-muted-foreground';
  const { tier } = keywordDifficultyTier(n);
  if (tier === 'super_hard') return 'text-rose-300';
  if (tier === 'hard') return 'text-amber-300';
  if (tier === 'medium') return 'text-emerald-300';
  return 'text-emerald-200';
};

export function KeywordRunDetailRoute(): JSX.Element {
  const { keywordRunId } = useParams({ strict: false }) as { keywordRunId: string };
  const run = useKeywordRun(keywordRunId);
  // Prime the project cache for breadcrumb resolution + Track CTA.
  const project = useProject(run.data?.project_id);
  const clusters = useKeywordRunClusters(keywordRunId);
  const [selectedCluster, setSelectedCluster] = useState<string | undefined>(undefined);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [rankBannerDismissed, setRankBannerDismissed] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('opportunity');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useAuthStore((s) => s.tenant);
  const { toast } = useToast();

  // A2 v2 masterclass filters — the "elephant filter" query surface.
  const [pillarsOnly, setPillarsOnly] = useState(false);
  const [maxDifficulty, setMaxDifficulty] = useState<number | undefined>(undefined);
  const [minVolume, setMinVolume] = useState<number | undefined>(undefined);
  const [maxSerpDa, setMaxSerpDa] = useState<number | undefined>(undefined);
  const [intentFilter, setIntentFilter] = useState<Intent[]>([]);

  // Phase 1 keyword-picker — bulk-track state.
  const [bulkOpen, setBulkOpen] = useState(false);

  const filters: KeywordFilters = {
    ...(selectedCluster ? { clusterId: selectedCluster } : {}),
    ...(pillarsOnly ? { pillarsOnly: true } : {}),
    ...(maxDifficulty !== undefined ? { maxDifficulty } : {}),
    ...(minVolume !== undefined ? { minVolume } : {}),
    ...(maxSerpDa !== undefined ? { maxSerpDa } : {}),
    ...(intentFilter.length > 0 ? { intent: intentFilter } : {}),
  };

  const keywords = useKeywordRunKeywords(keywordRunId, filters);
  // Always pull the full keyword set for the cluster bubbles' top-3 preview.
  const allKeywords = useKeywordRunKeywords(keywordRunId);
  const hasActiveFilter =
    pillarsOnly ||
    maxDifficulty !== undefined ||
    minVolume !== undefined ||
    maxSerpDa !== undefined ||
    intentFilter.length > 0;
  const clearFilters = (): void => {
    setPillarsOnly(false);
    setMaxDifficulty(undefined);
    setMinVolume(undefined);
    setMaxSerpDa(undefined);
    setIntentFilter([]);
  };

  const clusterList = clusters.data?.items ?? [];
  const keywordList = keywords.data?.items ?? [];

  const totals = useMemo(() => {
    const items = allKeywords.data?.items ?? [];
    const haveVolume = items.some((k) => k.volume !== null && k.volume > 0);
    const noiseCount = items.filter((k) => k.cluster_id === null).length;
    const topOpportunity = items.reduce(
      (acc, k) => Math.max(acc, k.opportunity ?? 0),
      0,
    );
    return {
      keywordCount: items.length,
      clusterCount: clusterList.length,
      noiseCount,
      topOpportunity,
      haveVolume,
    };
  }, [allKeywords.data, clusterList.length]);

  const runWarnings = useMemo(() => keywordRunWarnings(run.data), [run.data]);

  const rankCheckSkipped = useMemo(
    () => runWarnings.some((w) => RANK_CHECK_SKIP_CODES.has(w.code)),
    [runWarnings],
  );

  const rankCheckCompleted = useMemo(
    () =>
      runWarnings.some((w) => w.code === 'OWN_DOMAIN_GAPS' || w.code === 'OWN_DOMAIN_COVERED') ||
      (allKeywords.data?.items ?? []).some((k) => k.your_position != null),
    [runWarnings, allKeywords.data],
  );

  const ownDomainGapsWarning = useMemo(
    () => runWarnings.find((w) => w.code === 'OWN_DOMAIN_GAPS') ?? null,
    [runWarnings],
  );

  const sortedKeywords = useMemo(() => {
    const copy = [...keywordList];
    copy.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortKey];
      const bv = (b as unknown as Record<string, unknown>)[sortKey];
      if (sortKey === 'term') {
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      const an = typeof av === 'number' ? av : -Infinity;
      const bn = typeof bv === 'number' ? bv : -Infinity;
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return copy;
  }, [keywordList, sortKey, sortDir]);

  if (run.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={run.error}
          entityKind="keyword run"
          backTo="/keywords"
          backLabel="Keywords"
        />
      </div>
    );
  }
  if (run.isLoading || !run.data) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const r = run.data;

  const toggleSort = (key: SortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'term' ? 'asc' : 'desc');
    }
  };

  const handleDownloadCsv = async (): Promise<void> => {
    const params = new URLSearchParams();
    if (filters.clusterId) params.set('cluster_id', filters.clusterId);
    if (filters.maxDifficulty !== undefined)
      params.set('max_difficulty', String(filters.maxDifficulty));
    if (filters.minVolume !== undefined) params.set('min_volume', String(filters.minVolume));
    if (filters.maxSerpDa !== undefined) params.set('max_serp_da', String(filters.maxSerpDa));
    if (filters.intent && filters.intent.length > 0)
      params.set('intent', filters.intent.join(','));
    if (filters.pillarsOnly) params.set('pillars_only', 'true');
    const qs = params.toString();
    try {
      await downloadAuthed({
        url: `/v1/keyword-runs/${r.id}/export${qs ? `?${qs}` : ''}`,
        filename: `keywords-${r.id.slice(0, 8)}.csv`,
        accessToken,
        tenantId: tenant?.id,
      });
    } catch (e) {
      toastError(toast, e);
    }
  };

  const activeCluster = selectedCluster
    ? clusterList.find((c) => c.id === selectedCluster)
    : undefined;

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      <PageHeader
        title={describeSeed(r)}
        description={`Keyword ideas for ${r.country_code} · ${r.language}`}
        actions={
          <>
            <StatusPill status={r.status} />
            <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
              <Globe2 className="h-3.5 w-3.5" />
              {r.country_code}
            </span>
            <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
              <Languages className="h-3.5 w-3.5" />
              {r.language}
            </span>
            {project.data ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkOpen(true)}
                disabled={sortedKeywords.length === 0 || r.status !== 'completed'}
                title={sortedKeywords.length === 0 ? 'No keywords match current filters' : 'Bulk-track the currently filtered keywords'}
              >
                <Layers className="h-4 w-4" />
                Track filtered ({Math.min(sortedKeywords.length, 50)})
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={handleDownloadCsv}
              disabled={r.status !== 'completed'}
            >
              <Download className="h-4 w-4" />
              Download CSV
            </Button>
          </>
        }
      />

      {r.business_summary ? (
        <div className="flex items-start gap-2 rounded-lg border border-border/50 bg-muted/30 px-3 py-2 text-sm">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <div className="min-w-0">
            <span className="text-muted-foreground">Understood as: </span>
            <span className="italic">{r.business_summary}</span>
          </div>
        </div>
      ) : null}

      {!totals.haveVolume && !bannerDismissed && totals.keywordCount > 0 ? (
        <motion.div
          variants={fadeUp}
          className="relative flex items-start gap-3 rounded-xl border border-sky-500/30 bg-sky-500/5 p-4 text-sm text-sky-100"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" />
          <div className="flex-1">
            <div className="font-medium text-sky-100">
              Search volume data isn't turned on
            </div>
            <p className="mt-0.5 text-sky-100/80">
              To see monthly searches, cost-per-click, and difficulty for every keyword,
              connect your keyword data provider in{' '}
              <Link
                to="/settings/integrations"
                className="font-medium underline underline-offset-2 hover:text-sky-50"
              >
                Settings → Integrations
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            className="rounded-md p-1 text-sky-300 hover:bg-sky-500/10"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      ) : null}

      {rankCheckSkipped && !rankBannerDismissed && r.status === 'completed' ? (
        <motion.div
          variants={fadeUp}
          className="relative flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
          <div className="flex-1">
            <div className="font-medium text-amber-100">Own-domain rank check skipped</div>
            <p className="mt-0.5 text-amber-100/80">
              We couldn&apos;t check where your site ranks for these keywords (free mode or
              DataForSEO unavailable). Connect DataForSEO to see gaps vs your domain.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setRankBannerDismissed(true)}
            className="rounded-md p-1 text-amber-300 hover:bg-amber-500/10"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      ) : null}

      {ownDomainGapsWarning && !rankCheckSkipped ? (
        <motion.div
          variants={fadeUp}
          className="flex items-start gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4 text-sm text-emerald-100"
        >
          <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
          <div>
            <div className="font-medium text-emerald-100">Keyword gaps vs your domain</div>
            <p className="mt-0.5 text-emerald-100/80">{ownDomainGapsWarning.message}</p>
          </div>
        </motion.div>
      ) : null}

      <motion.section
        variants={fadeUp}
        className="grid grid-cols-2 gap-3 md:grid-cols-4"
      >
        {([
          { label: 'Keywords found', value: totals.keywordCount, icon: Sparkles, accent: 'sky' },
          { label: 'Topic groups', value: totals.clusterCount, icon: Layers, accent: 'violet' },
          { label: 'Unclustered', value: totals.noiseCount, icon: Compass, accent: 'amber' },
          {
            label: 'Best opportunity',
            value: totals.topOpportunity,
            icon: TrendingUp,
            accent: 'primary',
          },
        ] as Array<{ label: string; value: number; icon: typeof Sparkles; accent: KpiAccent }>).map(
          (k) => (
            <GlassSurface key={k.label} className="p-4">
              <KpiTile
                label={k.label}
                value={k.value.toLocaleString()}
                icon={k.icon}
                accent={k.accent}
              />
            </GlassSurface>
          ),
        )}
      </motion.section>

      <motion.section variants={fadeUp} className="space-y-3">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Topic groups</h2>
            <p className="text-sm text-muted-foreground">
              Related keywords grouped by meaning. Click one to filter the list below.
            </p>
          </div>
          {selectedCluster ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedCluster(undefined)}
            >
              <X className="h-3.5 w-3.5" />
              Clear filter
            </Button>
          ) : null}
        </div>
        {clusters.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : clusterList.length === 0 ? (
          <EmptyState
            title="Grouping your keywords..."
            description="Topic groups appear once we've analyzed how the keywords relate to each other."
          />
        ) : (
          <ClusterBubbles
            clusters={clusterList}
            keywords={allKeywords.data?.items ?? []}
            selectedId={selectedCluster}
            onSelect={setSelectedCluster}
          />
        )}
      </motion.section>

      <motion.section variants={fadeUp} className="space-y-3">
        <FilterBar
          pillarsOnly={pillarsOnly}
          onPillarsOnly={setPillarsOnly}
          maxDifficulty={maxDifficulty}
          onMaxDifficulty={setMaxDifficulty}
          minVolume={minVolume}
          onMinVolume={setMinVolume}
          maxSerpDa={maxSerpDa}
          onMaxSerpDa={setMaxSerpDa}
          intentFilter={intentFilter}
          onIntentFilter={setIntentFilter}
          onClear={clearFilters}
          hasActive={hasActiveFilter}
        />

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Keywords worth chasing</h2>
            <p className="text-sm text-muted-foreground">
              {activeCluster ? (
                <span className="inline-flex items-center gap-1.5">
                  <Filter className="h-3.5 w-3.5" />
                  showing only{' '}
                  <span className="font-medium text-foreground">{activeCluster.label}</span>
                </span>
              ) : (
                'Sorted by our best-bet score — high volume, lower difficulty first.'
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Settings2 className="h-3.5 w-3.5" />
            {sortedKeywords.length} rows
          </div>
        </div>

        {keywords.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : (
          <DataTable<Keyword>
            rows={sortedKeywords}
            rowKey={(k) => k.id}
            emptyState={
              <EmptyState
                title="No keywords match these filters"
                description="Try loosening the difficulty or volume limits, or clear the topic filter."
              />
            }
            columns={[
              {
                key: 'term',
                header: (
                  <SortLabel
                    label="Term"
                    sortKey="term"
                    active={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                  />
                ),
                cell: (k) => {
                  const cluster = clusterList.find((c) => c.id === k.cluster_id);
                  const isPillar = cluster?.pillar_keyword_id === k.id;
                  return (
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{k.term}</span>
                        {isPillar ? (
                          <Badge
                            variant="warning"
                            className="text-[9px] uppercase tracking-wide"
                            title="This is the main keyword for the group — target it on your pillar page."
                          >
                            Pillar
                          </Badge>
                        ) : null}
                      </div>
                      {cluster ? (
                        <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                          {cluster.label}
                        </div>
                      ) : null}
                    </div>
                  );
                },
              },
              {
                key: 'intent',
                header: 'Intent',
                cell: (k) =>
                  k.intent ? (
                    <Badge variant={INTENT_VARIANT[k.intent]} className="capitalize">
                      {k.intent}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  ),
              },
              {
                key: 'volume',
                header: (
                  <SortLabel
                    label="Volume"
                    sortKey="volume"
                    active={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                ),
                headClassName: 'text-right',
                className: 'text-right font-mono text-foreground/90',
                cell: (k) => (k.volume !== null ? k.volume.toLocaleString() : '—'),
              },
              {
                key: 'difficulty',
                header: (
                  <SortLabel
                    label="Difficulty"
                    sortKey="difficulty"
                    active={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                ),
                headClassName: 'text-right',
                className: cn('text-right font-mono'),
                cell: (k) => (
                  <span className={DIFFICULTY_TONE(k.difficulty)}>
                    {k.difficulty ?? '—'}
                  </span>
                ),
              },
              {
                key: 'serp_da',
                header: (
                  <span
                    title="How strong the top-ranking sites are (0–100). Lower means it's easier to break into the top results."
                    className="inline-flex items-center gap-1"
                  >
                    Competition
                  </span>
                ),
                headClassName: 'text-right',
                className: cn('text-right font-mono'),
                cell: (k) =>
                  k.serp_avg_da !== null ? (
                    <SerpDaCell value={k.serp_avg_da} topDomains={k.serp_top_domains} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  ),
              },
              {
                key: 'opportunity',
                header: (
                  <SortLabel
                    label="Opportunity"
                    sortKey="opportunity"
                    active={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                ),
                headClassName: 'text-right',
                className: 'text-right',
                cell: (k) => <OpportunityCell value={k.opportunity} />,
              },
              {
                key: 'your_position',
                header: (
                  <SortLabel
                    label="Your rank"
                    sortKey="your_position"
                    active={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                ),
                headClassName: 'text-right',
                className: 'text-right font-mono text-sm',
                cell: (k) => (
                  <YourPositionCell
                    position={k.your_position ?? null}
                    trackedPosition={k.tracked_position ?? null}
                    trackedProvider={k.tracked_provider ?? null}
                    trackedCheckedAt={k.tracked_checked_at ?? null}
                    rankCheckCompleted={rankCheckCompleted}
                    rankCheckSkipped={rankCheckSkipped}
                  />
                ),
              },
              // Phase 1 keyword-picker — per-row actions. Path A: launching a
              // brief or tracking directly from a row inherits keyword_id +
              // keyword_run_id via the defaultKeyword* props on both buttons,
              // so briefs get input_pins.keyword_id and tracked rows get their
              // FK columns populated automatically.
              {
                key: '__actions__',
                header: '',
                headClassName: 'text-right',
                className: 'text-right',
                cell: (k) =>
                  project.data ? (
                    <div className="flex items-center justify-end gap-1">
                      <CompetitorAnalysisCreateButton
                        projects={[project.data]}
                        defaultProjectId={project.data.id}
                        defaultKeyword={k.term}
                        defaultKeywordId={k.id}
                        defaultKeywordRunId={k.keyword_run_id}
                        buttonProps={{ size: 'sm', variant: 'ghost' }}
                        label="Analyze"
                      />
                      <BriefCreateButton
                        projectId={project.data.id}
                        defaultKeyword={k.term}
                        defaultKeywordId={k.id}
                        defaultKeywordRunId={k.keyword_run_id}
                        buttonProps={{ size: 'sm', variant: 'ghost' }}
                        label="Brief"
                      />
                      <TrackKeywordButton
                        projects={[project.data]}
                        defaultProjectId={project.data.id}
                        defaultKeyword={k.term}
                        defaultKeywordId={k.id}
                        defaultKeywordRunId={k.keyword_run_id}
                        buttonProps={{ size: 'sm', variant: 'ghost' }}
                        label="Track"
                      />
                    </div>
                  ) : null,
              },
            ] satisfies Array<DataTableColumn<Keyword>>}
            className="border-border/60 bg-card/40 backdrop-blur"
          />
        )}
      </motion.section>

      {bulkOpen && project.data ? (
        <TrackKeywordsBulkDialog
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          projectId={project.data.id}
          candidates={sortedKeywords.slice(0, 50).map(
            (k): BulkCandidate => ({
              keywordId: k.id,
              keywordRunId: k.keyword_run_id,
              term: k.term,
            }),
          )}
          title={`Track ${Math.min(sortedKeywords.length, 50)} keyword${sortedKeywords.length === 1 ? '' : 's'}`}
        />
      ) : null}
    </motion.div>
  );
}

function describeSeed(run: KeywordRun): string {
  const seed = run.seed;
  if (seed.type === 'domain') return seed.value;
  if (seed.type === 'topics') return seed.value.slice(0, 3).join(', ');
  return seed.value.slice(0, 80) + (seed.value.length > 80 ? '…' : '');
}

function keywordRunWarnings(
  run: KeywordRun | undefined,
): Array<{ code: string; message: string }> {
  if (!run?.warnings || !Array.isArray(run.warnings)) return [];
  return run.warnings.filter(
    (w): w is { code: string; message: string } =>
      typeof w === 'object' &&
      w != null &&
      typeof (w as { code?: unknown }).code === 'string' &&
      typeof (w as { message?: unknown }).message === 'string',
  );
}

function YourPositionCell({
  position,
  trackedPosition,
  trackedProvider,
  trackedCheckedAt,
  rankCheckCompleted,
  rankCheckSkipped,
}: {
  position: number | null;
  trackedPosition: number | null;
  trackedProvider: string | null;
  trackedCheckedAt: string | null;
  rankCheckCompleted: boolean;
  rankCheckSkipped: boolean;
}): JSX.Element {
  // Tracker rank wins whenever the keyword is being tracked — the Tracking
  // module uses Serper (free-tier) + DataForSEO, so a keyword can carry a
  // real signal even when the keyword-run's DFS check was skipped. A tracked
  // keyword with position=null means Serper checked it and the domain wasn't
  // in the top 100 — surface that as "Not in top 100" rather than a bare "—".
  const isTracked = trackedCheckedAt != null;
  if (isTracked) {
    const providerLabel = trackedProvider === 'dataforseo' ? 'DataForSEO' : trackedProvider === 'serper' ? 'Serper' : trackedProvider ?? 'Tracker';
    const checkedHint = trackedCheckedAt ? ` (checked ${new Date(trackedCheckedAt).toLocaleDateString()})` : '';
    if (trackedPosition != null) {
      return (
        <span
          className="text-foreground/90"
          title={`Tracked position via ${providerLabel}${checkedHint}`}
        >
          #{trackedPosition}
          <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            tracked
          </span>
        </span>
      );
    }
    return (
      <span
        className="cursor-help whitespace-nowrap text-xs font-medium text-amber-300 underline decoration-dotted decoration-muted-foreground/50 underline-offset-2"
        title={`Tracked via ${providerLabel}${checkedHint} — your domain is not in Google's top 100 for this keyword.`}
      >
        Not in top 100
        <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          tracked
        </span>
      </span>
    );
  }
  if (rankCheckSkipped) {
    return (
      <span
        className="cursor-help text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-2"
        title="DataForSEO not configured. Click Track on any row to have the Tracking module check its position via Serper (free) or DataForSEO."
      >
        —
      </span>
    );
  }
  if (!rankCheckCompleted) {
    return (
      <span className="text-muted-foreground" title="Rank check pending or in progress">
        —
      </span>
    );
  }
  if (position == null || position > 20) {
    return (
      <span className="text-amber-300" title="Your domain is not ranking in the top 20">
        Not ranking
      </span>
    );
  }
  return <span className="text-foreground/90">#{position}</span>;
}

function SortLabel({
  label,
  sortKey,
  active,
  dir,
  onClick,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: SortDir;
  onClick: (key: SortKey) => void;
  align?: 'left' | 'right';
}): JSX.Element {
  const isActive = active === sortKey;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      className={cn(
        'inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:text-foreground',
        align === 'right' && 'flex-row-reverse',
        isActive && 'text-foreground',
      )}
    >
      {label}
      {isActive ? (
        dir === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

function OpportunityCell({ value }: { value: number | null }): JSX.Element {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="inline-flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-border/50">
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary to-violet-400"
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="font-mono text-foreground">{value}</span>
    </div>
  );
}

function SerpDaCell({
  value,
  topDomains,
}: {
  value: number;
  topDomains: string[];
}): JSX.Element {
  const tone =
    value >= 70
      ? 'text-rose-300'
      : value >= 40
        ? 'text-amber-300'
        : 'text-emerald-300';
  const preview = topDomains.slice(0, 3).join(', ');
  return (
    <span
      className={tone}
      title={
        preview
          ? `Sites ranking on page 1 include: ${topDomains.slice(0, 10).join(', ')}`
          : undefined
      }
    >
      {value}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* A2 v2 masterclass filter bar                                        */
/* ------------------------------------------------------------------ */

const INTENT_OPTIONS: Intent[] = [
  'informational',
  'navigational',
  'commercial',
  'transactional',
];

function FilterBar(props: {
  pillarsOnly: boolean;
  onPillarsOnly: (v: boolean) => void;
  maxDifficulty: number | undefined;
  onMaxDifficulty: (v: number | undefined) => void;
  minVolume: number | undefined;
  onMinVolume: (v: number | undefined) => void;
  maxSerpDa: number | undefined;
  onMaxSerpDa: (v: number | undefined) => void;
  intentFilter: Intent[];
  onIntentFilter: (v: Intent[]) => void;
  hasActive: boolean;
  onClear: () => void;
}): JSX.Element {
  const toggleIntent = (i: Intent): void => {
    if (props.intentFilter.includes(i)) {
      props.onIntentFilter(props.intentFilter.filter((x) => x !== i));
    } else {
      props.onIntentFilter([...props.intentFilter, i]);
    }
  };
  return (
    <GlassSurface className="space-y-3 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Filter className="h-3.5 w-3.5" />
          Narrow the list
        </div>

        <FilterToggle
          label="Main keywords only"
          active={props.pillarsOnly}
          onClick={() => props.onPillarsOnly(!props.pillarsOnly)}
          title="Show only the primary keyword from each topic group."
        />

        <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <label className="uppercase tracking-wider">Max difficulty</label>
          <NumberInput
            value={props.maxDifficulty}
            onChange={props.onMaxDifficulty}
            placeholder="—"
            title="Hide keywords harder than this to rank for (0 = easy, 100 = very hard)."
          />
        </div>

        <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <label className="uppercase tracking-wider">Min searches</label>
          <NumberInput
            value={props.minVolume}
            onChange={props.onMinVolume}
            placeholder="—"
            title="Hide keywords below this monthly search volume."
          />
        </div>

        <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <label className="uppercase tracking-wider">Max competition</label>
          <NumberInput
            value={props.maxSerpDa}
            onChange={props.onMaxSerpDa}
            placeholder="—"
            title="Only show keywords where the top-ranking sites are weaker than this (0 = weak, 100 = very strong)."
          />
        </div>

        {props.hasActive ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={props.onClear}
            className="ml-auto text-xs"
          >
            <X className="h-3.5 w-3.5" />
            Clear all
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Intent
        </span>
        {INTENT_OPTIONS.map((i) => (
          <FilterToggle
            key={i}
            label={i}
            active={props.intentFilter.includes(i)}
            onClick={() => toggleIntent(i)}
            className="capitalize"
          />
        ))}
      </div>
    </GlassSurface>
  );
}

function FilterToggle({
  label,
  active,
  onClick,
  title,
  className,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  title?: string;
  className?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary/40 bg-primary/15 text-primary'
          : 'border-border/60 bg-card/40 text-muted-foreground hover:border-border hover:text-foreground',
        className,
      )}
    >
      {label}
    </button>
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
  title,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder: string;
  title?: string;
}): JSX.Element {
  return (
    <input
      type="number"
      min={0}
      max={100_000_000}
      value={value ?? ''}
      title={title}
      placeholder={placeholder}
      onChange={(e) => {
        const v = e.target.value.trim();
        if (v === '') return onChange(undefined);
        const n = Number(v);
        onChange(Number.isFinite(n) ? n : undefined);
      }}
      className="h-7 w-16 rounded-md border border-border/60 bg-card/40 px-2 text-right text-xs font-mono text-foreground outline-none transition-colors focus:border-primary/40"
    />
  );
}
